import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { AiSettingsService } from "@app/ai/services/ai-settings-data.service";
import { AiSettingConfig } from "@app/ai/shared/models/AiSettingConfig";
import { AiSettingsModel } from "@app/ai/shared/models/AiSettingModel";
import { AiSettingsType } from "@app/ai/shared/enums/AiSettingsType";
import { DateTimeFormats, LocalizationService } from "@app/shared";
import { firstValueFrom } from "rxjs";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { CerrixToggleInfoComponent } from "@app/shared/cerrix-toggle-info/cerrix-toggle-info.component";
import { SharedModule } from "@app/shared/shared.module";

@Component({
    selector: "ai-settings",
    templateUrl: "./ai-settings.component.html",
    styleUrls: ["./ai-settings.component.scss"],
    imports: [CerrixLoaderComponent, MenuSystemDirective, CerrixToggleInfoComponent, SharedModule],
})
export class AiSettingsComponent implements OnInit {
    private toastr = inject(ToastrService);
    private aiSettingsService = inject(AiSettingsService);
    private locService = inject(LocalizationService);
    promptService = inject(CerrixPromptService);

    @ViewChild("textPopupTemplate", { static: true }) textPopupTemplate: any;

    tab: TabModel;

    settingsConfig: AiSettingConfig[] = [];

    promptText: string;

    ngOnInit() {
        if (!this.tab.showLoader) {
            this.tab.showLoader = true;
        }

        Promise.all([
            firstValueFrom(this.aiSettingsService.getSettings()),
            firstValueFrom(this.aiSettingsService.getOutputLanguages()),
        ])
            .then(([settings, outputLanguages]) => {
                this.outputLanguageOptions = outputLanguages.map((l) => ({
                    ID: l.isoCode,
                    Name: l.name,
                }));
                this.parseSettings(settings);
            })
            .catch(() => {
                this.toastr.error("Failed to load AI settings");
            })
            .finally(() => {
                this.tab.showLoader = false;
            });
    }

    private parseSettings(settings: AiSettingsModel[]) {
        this.settingsConfig = settings.map((setting) => {
            const config = <AiSettingConfig>{
                id: setting.id,
                name: setting.name,
            };

            this.setSpecificProperties(config, setting);

            return config;
        });
    }

    private setSpecificProperties(config: AiSettingConfig, setting: AiSettingsModel) {
        switch (config.id) {
            case AiSettingsType.EnableAiFeatures: {
                this.setEnableAiFeaturesConfig(config, setting);
                break;
            }
            case AiSettingsType.OutputLanguage: {
                this.setOutputLanguageConfig(config, setting);
                break;
            }
        }
    }

    public showTermsAndConditions() {
        const promptConfig = this.getTermsAndConditionsPromptConfig();
        promptConfig.data.title = "Terms & Conditions";
        promptConfig.data.confirmButton.show = false;

        this.promptService.confirmCustom(promptConfig);
    }

    public beforeChangeEnableAiFeatures = async (currentValue: boolean) => {
        if (currentValue) {
            return true;
        }

        const result = await this.promptService
            .confirmCustom(this.getTermsAndConditionsPromptConfig())
            .toPromise();

        return !!result;
    };

    private setEnableAiFeaturesConfig(config: AiSettingConfig, setting: AiSettingsModel) {
        config.isToggle = true;
        config.boolValue = setting.boolValue;
        config.description =
            "By enabling AI in your CERRIX environment you accept the <u>terms & conditions</u>.";
        config.descriptionClick = this.showTermsAndConditions.bind(this);

        if (config.boolValue === true) {
            const formattedDateTime = this.locService.formatDateByEnum(
                setting.updatedOn,
                DateTimeFormats.DateShortTime,
            );
            config.toggleInfo = `Enabled and accepted by ${setting.updatedBy} at ${formattedDateTime}`;
        }

        config.beforeChange = this.beforeChangeEnableAiFeatures;
        config.valueChange = this.aiFeaturesToggleChanged.bind(this);
    }

    private getTermsAndConditionsPromptConfig() {
        this.promptText = `<p>By clicking ‘Accept’ below, you and your organization acknowledge and agree to the following disclaimer and the <a href="https://openai.com/policies/eu-terms-of-use/" target="blank">OpenAI terms of use</a>.</p> 

<p>The Customer’s designated Administrator confirms to CERRIX B.V. that they are the authorized representative and signatory or have gained approval from the authorized representative and signatory for the Customer, empowered to accept the terms and disclaimer of this service on the Customer’s behalf. This service is provided and supported by a third-party supplier. CERRIX B.V. disclaims any liability for the Customer’s use of this service, the accuracy of any input or output, or the compliance of such output with applicable laws and regulations. Ensuring compliance with all applicable laws and regulations is the sole responsibility of the Customer. The output generated by this service is not created by humans and should be carefully reviewed by the Customer before use.</p>
`;

        return {
            maxWidth: "800px",
            maxHeight: "480px",
            data: {
                title: "Accept Terms & Conditions",
                message: "",
                customTemplate: this.textPopupTemplate,
                cancelButton: {
                    text: "Close",
                },
                confirmButton: {
                    text: "Accept and enable AI",
                    show: true,
                },
            },
        };
    }

    async aiFeaturesToggleChanged(newValue: boolean) {
        if (this.tab.showLoader) {
            return;
        }

        await firstValueFrom(
            this.aiSettingsService.storeSetting({
                id: AiSettingsType.EnableAiFeatures,
                boolValue: newValue,
            }),
        );

        this.toastr.success("AI features have been " + (newValue ? "enabled" : "disabled"));

        this.tab.refresh();
    }

    private outputLanguageOptions: { ID: string; Name: string }[] = [];

    private setOutputLanguageConfig(config: AiSettingConfig, setting: AiSettingsModel) {
        config.isToggle = false;
        config.isDropdown = true;
        config.description = "The language the AI should output in";
        config.dropdownOptions = this.outputLanguageOptions;
        config.dropdownValue = setting.stringValue ?? "en";
        config.valueChange = this.outputLanguageChanged.bind(this, config);
    }

    async outputLanguageChanged(config: AiSettingConfig, newValue: string) {
        if (this.tab.showLoader) {
            return;
        }

        if (config.dropdownValue === newValue) {
            return;
        }

        await firstValueFrom(
            this.aiSettingsService.storeSetting({
                id: AiSettingsType.OutputLanguage,
                stringValue: newValue,
            }),
        );

        config.dropdownValue = newValue;
        this.toastr.success("Output language has been updated.");
    }
}
